import { HTMLAttributes, ReactNode } from 'react';

const variants = {
  default: 'bg-white dark:bg-dark-card',
  elevated: 'bg-white dark:bg-dark-card shadow-elevated dark:shadow-dark-elevated',
  bordered: 'bg-white dark:bg-dark-card border-2',
} as const;

type CardVariant = keyof typeof variants;

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  variant?: CardVariant;
  className?: string;
  padding?: boolean;
}

export interface CardSubComponentProps {
  children?: ReactNode;
  className?: string;
}

function Card({ children, variant = 'default', className = '', padding = true, ...props }: CardProps) {
  const baseStyles =
    'border border-light-border dark:border-dark-border rounded-lg shadow-card dark:shadow-dark-card';

  const paddingStyles = padding ? 'p-4 sm:p-6' : '';

  return (
    <div
      className={`${baseStyles} ${variants[variant]} ${paddingStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function CardHeader({ children, className = '' }: CardSubComponentProps) {
  return (
    <div className={`flex items-center justify-between pb-4 ${className}`}>
      {children}
    </div>
  );
}

function CardTitle({ children, className = '' }: CardSubComponentProps) {
  return (
    <h3 className={`text-lg font-semibold text-light-text dark:text-dark-text ${className}`}>
      {children}
    </h3>
  );
}

function CardDescription({ children, className = '' }: CardSubComponentProps) {
  return (
    <p className={`text-sm text-light-muted dark:text-dark-muted ${className}`}>
      {children}
    </p>
  );
}

function CardContent({ children, className = '' }: CardSubComponentProps) {
  return <div className={className}>{children}</div>;
}

function CardFooter({ children, className = '' }: CardSubComponentProps) {
  return (
    <div
      className={`flex items-center justify-end gap-2 pt-4 border-t border-light-border dark:border-dark-border ${className}`}
    >
      {children}
    </div>
  );
}

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Content = CardContent;
Card.Footer = CardFooter;

export default Card;
